const DB_NAME = 'edosar_e2ee_keys';
const DB_VERSION = 1;
const STORE = 'keys';
const PRIVATE_KEY_ID = 'rsa_oaep_private';
const PASSKEY_WRAP_ID = 'passkey_wrapped_jwk';

type StoredKeyRecord = {
  id: string;
  key: CryptoKey;
  created_at: number;
};

export type PasskeyWrappedJwkRecord = {
  id: string;
  credential_id_b64u: string;
  prf_salt_b64: string;
  jwk_ciphertext_b64: string;
  jwk_iv_b64: string;
  created_at: number;
};

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('Failed to open IndexedDB'));
  });
}

async function withStore<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const store = tx.objectStore(STORE);
    const req = fn(store);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('IndexedDB request failed'));
    tx.oncomplete = () => db.close();
    tx.onerror = () => {
      try {
        db.close();
      } catch {
        // ignore
      }
    };
  });
}

export async function getRememberedPrivateKey(): Promise<CryptoKey | null> {
  try {
    const rec = (await withStore<StoredKeyRecord | undefined>('readonly', (s) => s.get(PRIVATE_KEY_ID))) as
      | StoredKeyRecord
      | undefined;
    return rec?.key || null;
  } catch {
    return null;
  }
}

export async function setRememberedPrivateKey(key: CryptoKey): Promise<void> {
  const rec: StoredKeyRecord = { id: PRIVATE_KEY_ID, key, created_at: Date.now() };
  await withStore('readwrite', (s) => s.put(rec));
}

export async function clearRememberedPrivateKey(): Promise<void> {
  try {
    await withStore('readwrite', (s) => s.delete(PRIVATE_KEY_ID));
  } catch {
    // ignore
  }
}

export async function getPasskeyWrappedJwk(): Promise<PasskeyWrappedJwkRecord | null> {
  try {
    const rec = (await withStore<PasskeyWrappedJwkRecord | undefined>('readonly', (s) => s.get(PASSKEY_WRAP_ID))) as
      | PasskeyWrappedJwkRecord
      | undefined;
    return rec || null;
  } catch {
    return null;
  }
}

export async function setPasskeyWrappedJwk(rec: Omit<PasskeyWrappedJwkRecord, 'id' | 'created_at'>): Promise<void> {
  const row: PasskeyWrappedJwkRecord = { id: PASSKEY_WRAP_ID, created_at: Date.now(), ...rec };
  await withStore('readwrite', (s) => s.put(row));
}

export async function clearPasskeyWrappedJwk(): Promise<void> {
  try {
    await withStore('readwrite', (s) => s.delete(PASSKEY_WRAP_ID));
  } catch {
    // ignore
  }
}

export async function clearAllRememberedKeys(): Promise<void> {
  await clearRememberedPrivateKey();
  await clearPasskeyWrappedJwk();
}

